import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { Layout } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { getTextbooks, getChapters } from "@/lib/api/textbooks"
import { setUserProgress } from "@/lib/api/user"
import { ProgressPicker } from "@/components/textbook-progress-picker"

function Welcome() {
  const navigate = useNavigate()
  const [selectedTextbookId, setSelectedTextbookId] = useState<string | null>(
    null
  )
  const [selectedChapterNumber, setSelectedChapterNumber] = useState<
    number | null
  >(null)

  const { data: textbooks = [] } = useQuery({
    queryKey: ["textbooks"],
    queryFn: getTextbooks,
  })

  const { data: chapters = [] } = useQuery({
    queryKey: ["chapters", selectedTextbookId],
    queryFn: () => getChapters(selectedTextbookId!),
    enabled: !!selectedTextbookId,
  })

  const { mutate: finish, isPending } = useMutation({
    mutationFn: ({
      textbookId,
      chapterNumber,
    }: {
      textbookId: string
      chapterNumber: number
    }) => setUserProgress({ textbookId, chapterNumber }),
    onSuccess: () => navigate({ to: "/stories" }),
  })

  return (
    <Layout>
      <ProgressPicker
        textbooks={textbooks}
        selectedTextbookId={selectedTextbookId}
        onTextbookChange={(id) => {
          setSelectedTextbookId(id)
          setSelectedChapterNumber(null)
        }}
        chapters={chapters}
        selectedChapterNumber={selectedChapterNumber}
        onChapterSelect={setSelectedChapterNumber}
        heading={
          <div className="w-full md:w-1/2" style={{ letterSpacing: "-2px" }}>
            Tell us where you are in&nbsp;
            <span
              style={{ fontWeight: 500 }}
              className="text-[oklch(0.62_0.09_35)]"
            >
              your studies
            </span>
          </div>
        }
        description="Pick your textbook and the last chapter you've worked through. Yomimono will only use vocabulary, kanji and grammar from those chapters when it writes stories for you."
        footer={
          <div className="flex justify-end">
            <Button
              onClick={() =>
                selectedTextbookId &&
                selectedChapterNumber !== null &&
                finish({
                  textbookId: selectedTextbookId,
                  chapterNumber: selectedChapterNumber,
                })
              }
              disabled={isPending || selectedChapterNumber === null}
            >
              Apply
            </Button>
          </div>
        }
      />
    </Layout>
  )
}

export const Route = createFileRoute("/_authenticated/welcome")({
  component: Welcome,
})
